import React, { Dispatch } from 'react';
import { Template } from '~/src/design/template';

/**
 * Actions
 */
export enum BULK_DESIGN_VIEW_MODE {
	DEFAULT,
	LIST_VIEW,
	DESIGN,
	PRODUCTION_DOCUMENTS,
	ADDITIONAL_FIELD,
	MORE_SPACE,
}

export const SET_SERVER_TEMPLATES = 'SET_SERVER_TEMPLATES';
export const SHOW_ALERT = 'SHOW_ALERT';
export const HIDE_ALERT = 'HIDE_ALERT';
export const TOGGLE_VIEW_MODE = 'TOGGLE_VIEW_MODE';
export const SET_BULK_DESIGN_LOADING = 'SET_BULK_DESIGN_LOADING';
export const SHOW_DATA_PANEL = 'SHOW_DATA_PANEL';
export const HIDE_DATA_PANEL = 'HIDE_DATA_PANEL';
export const SET_DATA_PANEL_WIDTH = 'SET_DATA_PANEL_WIDTH';

const DEFAULT_WIDTH = 300;

/**
 * Initial State
 */
export interface AlertDataType {
	open: boolean;
	title: string;
	type: 'success' | 'error',
	description?: string;
	autoDuration?: number;
}
export interface BulkDesignStore {
	mainViewMode: BULK_DESIGN_VIEW_MODE;
	serverTemplates: Template[];
	alertProps: AlertDataType;
	loading: boolean;
	dataPanelWidth: number;
	dispatch: Dispatch<BulkDesignAction>;
}
export const INITIAL_STATE: BulkDesignStore = {
	mainViewMode: BULK_DESIGN_VIEW_MODE.DEFAULT,
	serverTemplates: [],
	loading: false,
	alertProps: {
		open: false,
		title: '',
		type: 'success',
		description: '',
		autoDuration: 6000,
	},
	dataPanelWidth: DEFAULT_WIDTH,
	dispatch: () => ({}),
};

/**
 * Context
 */
const BulkDesignContext = React.createContext<BulkDesignStore>(INITIAL_STATE);

export { BulkDesignContext };

/**
 * Reducer
 */
interface BulkDesignActionType<T, P> {
	type: T;
	data?: P;
}

export type BulkDesignAction =
	| BulkDesignActionType<typeof SET_SERVER_TEMPLATES, Template[]>
	| BulkDesignActionType<typeof SHOW_ALERT, AlertDataType>
	| BulkDesignActionType<typeof HIDE_ALERT, void>
	| BulkDesignActionType<typeof TOGGLE_VIEW_MODE, BULK_DESIGN_VIEW_MODE>
	| BulkDesignActionType<typeof SET_BULK_DESIGN_LOADING, boolean>
	| BulkDesignActionType<typeof SHOW_DATA_PANEL, void>
	| BulkDesignActionType<typeof HIDE_DATA_PANEL, void>
	| BulkDesignActionType<typeof SET_DATA_PANEL_WIDTH, number>;

export const reducer = (
	state: BulkDesignStore,
	action: BulkDesignAction
): BulkDesignStore => {
	switch (action.type) {
	case SET_SERVER_TEMPLATES:
		return {
			...state,
			serverTemplates: action.data as Template[],
		};
	case SHOW_ALERT:
		return {
			...state,
			alertProps: {
				...state.alertProps,
				...(action.data as AlertDataType),
			},
		};
	case HIDE_ALERT:
		return {
			...state,
			alertProps: {
				open: false,
				type: 'success',
				title: '',
				description: '',
				autoDuration: 3000,
			},
		};
	case TOGGLE_VIEW_MODE:
		return {
			...state,
			mainViewMode:
					action.data === state.mainViewMode
						? BULK_DESIGN_VIEW_MODE.DEFAULT
						: (action.data as BULK_DESIGN_VIEW_MODE),
		};
	case SET_BULK_DESIGN_LOADING:
		return {
			...state,
			loading: action.data as boolean,
		};
	case SHOW_DATA_PANEL:
		return {
			...state,
			dataPanelWidth: DEFAULT_WIDTH,
		};
	case HIDE_DATA_PANEL:
		return {
			...state,
			dataPanelWidth: 0,
		};
	case SET_DATA_PANEL_WIDTH:
		return {
			...state,
			dataPanelWidth: action.data as number,
		};
	default:
		return state;
	}
};
